import * as Sentry from '@sentry/vue'
import { computed, ref } from 'vue'
import { t } from '@/i18n/t'
import { api } from '@/utils/apiClient'
import { errorMessage } from '@/utils/apiError'

// Minimal current-admin composable. Fetches /api/admin/me once per
// mount; exposes the fields the dashboard needs to decide what
// buttons to render (e.g. "you can edit your own entries"). Module-
// scoped so multiple consumers share one fetch.

// Role tiers — owner is the migrated successor to the pre-#owner-role
// "admin" tier (full power including billing). Admin is the new
// delegated ops role (everything except billing + brand). Advisor is
// the individual-contributor seat. See internal/auth/jwt.go for the
// source of truth.
export type Role = 'owner' | 'admin' | 'advisor'

export interface CurrentAdmin {
  id: string
  email: string
  // name is the operator's profile name (admin_users.name). Empty
  // string when not yet set — surface accordingly (e.g. fall back
  // to email-localpart when threading to Whereby's displayName).
  name: string
  role: Role
  factor: string
  // authOrgSlug is the slug of the org the JWT actually belongs to,
  // looked up server-side from claims.OrgID. Distinct from any
  // URL/subdomain-derived slug. The router guard compares this
  // against window.location.hostname's first label to detect
  // cross-tenant cookie-leak situations (apex-shared cookies, #825).
  // Empty string when the backend couldn't resolve (test mode, or
  // org row missing).
  authOrgSlug: string
  // hasPasskey is true when the operator has ≥1 webauthn credential
  // enrolled (server-side count, tenant-scoped). The router's
  // shouldRequirePasskey guard uses this to decide between
  // "use existing passkey" (/auth/login) and "enroll new"
  // (/auth/passkey) for a magic_link / recovery session that needs
  // to upgrade its factor. Defaults to false on missing/legacy
  // responses — the conservative path is to send the operator
  // through enrollment, which still works for the no-credential case.
  // See #1272.
  hasPasskey: boolean
  // calendarHourRange — per-admin dashboard-calendar hour-range
  // preference (admin_users.calendar_hour_range). 'all' renders the
  // full 00-23 grid; 'working' renders 08-17 expanded outward by the
  // operator's working windows. Defaults to 'all' on missing/legacy
  // responses.
  calendarHourRange: 'all' | 'working'
  // calendarShowHolidays — toggle for Norwegian holiday entries +
  // red-day styling in the dashboard calendar. Defaults to true.
  calendarShowHolidays: boolean
  // isStaff is true when the operator is also an active stemne_staff
  // member. Rides on /me so the sidebar's staff jump-link doesn't need
  // a second /api/staff/me probe (which 403'd in every ordinary
  // operator's console). Defaults to false on missing/legacy responses.
  isStaff: boolean
  // isDemoOrg mirrors organizations.is_demo for the SESSION's org — i.e.
  // "am I looking at the sales-demo tenant", regardless of how I logged
  // in. Distinct from the factor-based demo check (isDemo), which is
  // "am I a self-serve demo VISITOR". The live view uses this to show a
  // demo placeholder for seeded video bookings that never mint a Whereby
  // room. Defaults to false on missing/legacy responses.
  isDemoOrg: boolean
  // subscriptionStatus mirrors organizations.subscription_status for the
  // SESSION's org (from the tenant resolver, same column the backend
  // write-gate reads). The router's subscription gate reads it to bounce
  // a frozen operator to the /abonnement interstitial before the
  // dashboard paints. Empty string on missing/legacy responses →
  // isSubscriptionInactive treats that as "not frozen", so a stale
  // backend never traps a paying operator behind the paywall.
  subscriptionStatus: string
  // Optional history signals trim advanced navigation for fresh orgs. They
  // remain optional so a legacy /me response degrades open instead of hiding
  // a destination from an established operator.
  hasInvoiceBasisActivity?: boolean
  hasPaymentDisputeHistory?: boolean
}

const current = ref<CurrentAdmin | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)

// In-flight promise dedupe (#1272 / O3g). Multiple components mounting
// in the same tick each call fetchCurrentAdmin(); without this they'd
// each fire their own /api/admin/me round-trip. Shared promise → one
// network call, N awaiters resolve from the same response.
//
// Reset to null in the finally block so the NEXT call after resolution
// triggers a fresh fetch. The always-revalidate contract (O3g) means
// fetchCurrentAdmin() itself never short-circuits on a populated
// `current.value`; CALLERS decide whether to skip via isCurrentAdminStale.
let inFlight: Promise<CurrentAdmin | null> | null = null

// Last successful fetch timestamp. Drives isCurrentAdminStale so the
// router's background revalidate only fires when the cache has aged out.
// 0 = never fetched (or invalidated) → stale → next call fetches.
let lastFetchedAt = 0

// 5 minute cache TTL. Long enough that hot-tap navigation skips the
// background /me on every step; short enough that a flag/role change
// (or anything not caught by the 401 interceptor + explicit invalidate)
// surfaces within one coffee-break. Paired with the tab-focus refresh
// in App.vue: when the operator comes back to the tab, we re-fetch
// regardless of staleness.
const CURRENT_ADMIN_STALE_MS = 5 * 60 * 1000

export function isCurrentAdminStale(): boolean {
  return lastFetchedAt === 0 || Date.now() - lastFetchedAt > CURRENT_ADMIN_STALE_MS
}

// isOps returns true for owner or admin. Mirrors auth.IsOps on the
// backend — the day-to-day operational gate (bookings, customers,
// services, team for non-owners). Standalone helper so non-component
// callers (composables, route guards) can use it without setting up
// the full useCurrentAdmin reactive scope.
export function isOps(role?: Role | null): boolean {
  return role === 'owner' || role === 'admin'
}

// isOwner is the owner-only gate (billing, brand, plan changes,
// inviting other owners). Strict equality so a future fourth tier
// doesn't silently inherit owner privileges.
export function isOwner(role?: Role | null): boolean {
  return role === 'owner'
}

// roleLabel renders the role for UI surfaces. Norwegian copy lives
// in i18n; this helper keeps the mapping in one place so the chip
// labels (Eier / Administrator / Rådgiver) can't drift between sites.
export function roleLabel(role?: Role | null): string {
  switch (role) {
    case 'owner':
      return t('composablesUseCurrentAdmin.roleOwner')
    case 'admin':
      return t('composablesUseCurrentAdmin.roleAdmin')
    case 'advisor':
      return t('composablesUseCurrentAdmin.roleAdvisor')
    default:
      return ''
  }
}

// invalidateCurrentAdmin clears the module-scoped cache so the next
// fetchCurrentAdmin() call re-hits /api/admin/me. Call this after any
// operation that flips backend auth state (passkey enrollment promotes
// the session from magic_link → passkey factor; without invalidation
// the cached `factor` stays stale and the router's RequirePasskeyFactor
// mirror redirects valid passkey sessions back to /auth/passkey in an
// infinite loop).
//
// Standalone helper (not part of useCurrentAdmin()) so callers don't
// need to allocate the reactive scope just to invalidate. Exported so
// PasskeyEnrollView, DashboardAccountView, and any future "auth state
// changed" surface can drop a one-line cache bust after their own
// success path.
export function invalidateCurrentAdmin(): void {
  current.value = null
  error.value = null
  // Reset the stale clock so the next call is treated as the first
  // fetch (blocks-and-awaits in the router gate).
  lastFetchedAt = 0
  // The in-flight promise is deliberately NOT cancelled here. An
  // already-running fetch will finish and update `current` to the
  // newly-fetched identity; if the caller wanted hard-stop semantics
  // they could check current.value === null in the await, but no
  // current consumer needs that. Resetting just the visible state is
  // sufficient for the "session revoked, flip the UI now" flow.
}

export function useCurrentAdmin() {
  async function fetchCurrentAdmin(): Promise<CurrentAdmin | null> {
    // In-flight dedupe (O3g). A concurrent caller within the same
    // round-trip window returns the SAME promise so we don't fire two
    // /api/admin/me requests for one tick of component mounts.
    if (inFlight) return inFlight

    inFlight = (async () => {
      loading.value = true
      error.value = null
      try {
        const res = await api.get<{
          admin: {
            id: string
            email: string
            name: string
            role: Role
            has_passkey?: boolean
            calendar_hour_range?: string
            calendar_show_holidays?: boolean
          }
          factor: string
          auth_org_slug?: string
          is_staff?: boolean
          is_demo?: boolean
          subscription_status?: string
          has_invoice_basis_activity?: boolean
          has_payment_dispute_history?: boolean
        }>('/api/admin/me')
        current.value = {
          id: res.admin.id,
          email: res.admin.email,
          name: res.admin.name ?? '',
          role: res.admin.role,
          factor: res.factor,
          authOrgSlug: res.auth_org_slug ?? '',
          hasPasskey: res.admin.has_passkey ?? false,
          calendarHourRange: res.admin.calendar_hour_range === 'working' ? 'working' : 'all',
          calendarShowHolidays: res.admin.calendar_show_holidays ?? true,
          isStaff: res.is_staff ?? false,
          isDemoOrg: res.is_demo ?? false,
          subscriptionStatus: res.subscription_status ?? '',
          hasInvoiceBasisActivity: res.has_invoice_basis_activity,
          hasPaymentDisputeHistory: res.has_payment_dispute_history,
        }
        lastFetchedAt = Date.now()
        // Which org the session authenticated against, what the operator
        // is allowed to do, and whether this is a staff member looking at
        // someone else's tenant — the three questions asked first on any
        // admin-surface error.
        //
        // Email and admin id are deliberately NOT attached. The feedback
        // widget already asks for an email when someone chooses to give
        // one; auto-tagging every background error with the operator's
        // account email is a different bargain. Safe no-op when no Sentry
        // client is active (DSN unset in local dev).
        Sentry.setContext('operator', {
          authOrgSlug: current.value.authOrgSlug,
          role: current.value.role,
          isStaff: current.value.isStaff,
        })
        return current.value
      } catch (e) {
        // O3g — failure does NOT clear `current.value`. A transient
        // network hiccup must not flip the UI to "logged out"; only an
        // explicit invalidate (called by apiClient on a confirmed
        // session-rejected 401) does that. Surface the error for
        // diagnostics, return null for this caller, but leave any
        // existing cached identity alone.
        error.value = errorMessage(e)
        return null
      } finally {
        loading.value = false
        inFlight = null
      }
    })()
    return inFlight
  }

  // Reactive role helpers. Use these in components to avoid sprinkling
  // role-string literals everywhere. They re-compute when /api/admin/me
  // resolves, so a template like `<BillingTab v-if="canManageBilling" />`
  // hides cleanly during the loading window (null → false).
  const isOpsRef = computed(() => isOps(current.value?.role))
  const isOwnerRef = computed(() => isOwner(current.value?.role))
  const canManageBilling = isOwnerRef // alias for readability at call sites
  const canManageBrand = isOwnerRef
  // Demo sessions (self-serve sales demo, auth factor "demo") get a few
  // surfaces trimmed: a welcome modal on landing, and no real Google
  // Calendar connect. Reuse this so the demo checks stay in one place.
  const isDemoRef = computed(() => current.value?.factor === 'demo')
  // isDemoOrg is "the org I'm in is the sales-demo tenant", independent
  // of my login factor — so a staff member or salesperson demoing with
  // their own credentials also gets demo-tenant affordances. See the
  // CurrentAdmin.isDemoOrg comment for the isDemo (factor) distinction.
  const isDemoOrgRef = computed(() => !!current.value?.isDemoOrg)

  return {
    current,
    loading,
    error,
    fetchCurrentAdmin,
    isOps: isOpsRef,
    isOwner: isOwnerRef,
    canManageBilling,
    canManageBrand,
    isDemo: isDemoRef,
    isDemoOrg: isDemoOrgRef,
  }
}
